import { isNil, isNotNil } from 'ramda'
import { cloneElement, isValidElement } from 'react'

import { IconProps } from '~/src/components/generic/PhosphorIcons/index'

import {
  DEFAULT_TIME_ZONE_NAME,
  LEG_COLORS,
  OPERATIONAL_STOP,
  TRACTOR_TYPE_TO_NAME,
  TWENTY_FOUR_HOURS_IN_SECONDS,
  VIOLATION_EXCEPTION_TYPES,
} from '~/src/components/shared/features/constants'
import {
  ExceptionTypes,
  FreightStatus,
  LegForLatenessViolations,
  StopStatus as StopProgressStatus,
  TrailerInfo,
} from '~/src/components/shared/features/types'
import {
  DetailedLegForViolations,
  ExceptionForViolations,
  ExceptionTypename,
  LegForViolations,
  LoadForViolations,
  Violation,
} from '~/src/components/shared/features/hooks/types'
import { View } from '~/src/components/shared/Notification/types'
import {
  iconMap,
  ViolationDisplayText,
} from '~/src/components/shared/Notification/constants'

import {
  convertToTimezone,
  diffInSeconds,
  isDateBeforeComparison,
  parseDate,
} from '~/helpers/date'
import { formatAddress } from '~/helpers/location'
import { enumToPretty } from '~/helpers/string'

import {
  Document,
  DocumentType,
  GpsPoint,
  LegStatus,
  LoadStatus,
  NotificationType,
  OsdRecordStatus,
  RiskTier,
  ScosStopAction,
  ShipmentStatus,
  SortInput,
  StopLatenessStatus,
  StopReference,
  StopStatus,
} from '~/__generated_types__/globalTypes'

import { ListStop } from '~/src/components/RyderShare/TrackingPage/types'

// TODO: Add tests for all of these helper functions missing them:
// https://app.asana.com/0/1206283941353593/1206676201369455
const getOppositeDirection = (direction: string): SortInput => {
  if (direction === SortInput.None) {
    return SortInput.Asc
  } else if (direction === SortInput.Asc) {
    return SortInput.Desc
  }

  return SortInput.None
}

const capitalize = (str: string): string => {
  const lowerCaseString = str.toLowerCase()

  return lowerCaseString.charAt(0).toUpperCase() + lowerCaseString.slice(1)
}

const docCreatedAtComparator = (docA: Document, docB: Document): number => {
  if (docA.createdAt && docB.createdAt) {
    return Date.parse(docB.createdAt) - Date.parse(docA.createdAt)
  } else return 0
}

/**
 * Returns the best available document based on the following priority:
 * 1. If no documents are provided, returns null
 * 2. The most recent DocumentType.Mb type doc
 * 3. The most recent DocumentType.ProofOfDelivery doc, prioritizing docs with 'EPOD' in the name
 * 4. The most recent document of any DocumentType
 */
const getBestProofOfDeliveryDoc = (docs: Array<Document>): Document | null => {
  if (docs.length === 0) return null

  const mbDocs = docs.filter((doc) => doc.type === DocumentType.Mb)
  if (mbDocs.length > 0) {
    return mbDocs.sort(docCreatedAtComparator)[0]
  }

  const podDocs = docs.filter((doc) => doc.type === DocumentType.ProofOfDelivery)
  if (podDocs.length > 0) {
    podDocs.sort((docA, docB) => {
      const isEpodA = docA.name?.toUpperCase().includes('EPOD')
      const isEpodB = docB.name?.toUpperCase().includes('EPOD')

      if (isEpodA && !isEpodB) return -1
      if (!isEpodA && isEpodB) return 1

      return docCreatedAtComparator(docA, docB)
    })

    return podDocs[0]
  }

  return docs.sort(docCreatedAtComparator)[0]
}

const getDocumentFilterType = (docType?: DocumentType | null) => {
  if (
    docType === DocumentType.ProofOfDelivery ||
    docType === DocumentType.BillOfLading ||
    docType === DocumentType.Tender
  ) {
    return docType
  }

  return DocumentType.Other
}

const getLocaleTimeString = (
  time: Date | string | undefined | null,
  locale = 'en-US'
) => {
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }

  if (time instanceof Date) {
    return time.toLocaleTimeString(locale, options)
  }

  return time ? new Date(time).toLocaleTimeString(locale, options) : undefined
}

const getLocaleDateString = (
  date: Date | string | undefined | null,
  option?: Intl.DateTimeFormatOptions,
  locale = 'en-US'
): string | undefined => {
  const defaultOption: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    day: '2-digit',
  }

  if (date instanceof Date) {
    return date.toLocaleDateString(locale, option ?? defaultOption)
  }

  return date
    ? new Date(date).toLocaleDateString(locale, option ?? defaultOption)
    : undefined
}

// TODO: standardize date and time formatting across the app
// https://app.asana.com/1/1187292652299643/project/1209572988416484/task/1210893576993950?focus=true
/**
 * Extracts the date and time from a datetime string.
 *
 * The function expects the `initialDateTime` string to be in the format "MM/DD/YYYY HH:mm:ss" (or similar),
 * separated by a space. Constructs a `Date` object if the date is valid. The time part is returned as a string.
 *
 * @param initialDateTime - The datetime string to parse, or `undefined`.
 * @returns An object containing:
 * - `date`: A `Date` object if the date is valid, otherwise `undefined`.
 * - `time`: The time part of the string if present, otherwise `undefined`.
 */
const getDateAndTimeFromDatetime = (
  initialDateTime: string | undefined
): { date: Date | undefined; time: string | undefined } => {
  if (!initialDateTime) {
    return { date: undefined, time: undefined }
  }

  const [datePart, timePart] = initialDateTime.split(' ')
  const { month, day, year } = parseDate(datePart)
  const date = month && day && year ? new Date(year, month - 1, day) : undefined
  const time = timePart

  return { date, time }
}

// TODO: standardize date and time formatting across the app
// https://app.asana.com/1/1187292652299643/project/1209572988416484/task/1210893576993950?focus=true
/**
 * Ensures that the provided ISO date-time string is in UTC format by appending a 'Z' suffix if necessary.
 *
 * @param dateTime - The ISO date-time string to convert to UTC format.
 * @returns The date-time string in UTC format (with 'Z' suffix if not already present or offset).
 */
const getUTCDateString = (dateTime: string): string => {
  return dateTime.includes('Z') || dateTime.includes('+') ? dateTime : dateTime + 'Z'
}

// TODO: standardize date and time formatting across the app
// https://app.asana.com/1/1187292652299643/project/1209572988416484/task/1210893576993950?focus=true
/**
 * Returns the short time zone code (e.g., "PST", "GMT") for a given UTC date string and IANA time zone.
 *
 * @param utcString - The UTC date string to format.
 * @param timeZone - The IANA time zone identifier (e.g., "America/Los_Angeles").
 * @returns The short time zone code corresponding to the provided time zone, or 'UTC' if unavailable or on error.
 */
const getTimeZoneCode = (utcString: string, timeZone: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short',
      timeZone,
    })
    const parts = formatter.formatToParts(new Date(utcString))

    const timeZonePart = parts.find((part) => part.type === 'timeZoneName')

    return timeZonePart?.value || 'UTC'
  } catch {
    return 'UTC'
  }
}

// TODO: standardize date and time formatting across the app
// https://app.asana.com/1/1187292652299643/project/1209572988416484/task/1210893576993950?focus=true
/**
 * Formats a date object into a string with time and optional date, including the time zone code.
 *
 * @param date - The date object to format.
 * @param options - Options to include date and specify time zone code.
 * @returns A formatted string with time and optional date, including the time zone code.
 */
const formatDateTime = (
  date: Date,
  options: { includeDate: boolean; tzCode: string }
): string => {
  const timeString = getLocaleTimeString(date)

  if (options.includeDate) {
    const dateString = getLocaleDateString(date, { month: 'numeric', day: 'numeric' })

    return `${timeString} ${options.tzCode}, ${dateString}`
  }

  return `${timeString} ${options.tzCode}`
}

// TODO: standardize date and time formatting across the app
// https://app.asana.com/1/1187292652299643/project/1209572988416484/task/1210893576993950?focus=true
/**
 * Converts UTC time strings to localized date/time strings for audit trail events.
 *
 * @param dateTime - UTC time string
 * @param includeDate - Whether to include the date portion in the output
 * @param timeZoneName - Optional timezone name for formatting, falls back to client local time if not provided
 * @returns Formatted time string like "14:03 MST" or "14:03 MST, 07/22" if includeDate is true
 */
const getLocaleDateTimeString = (
  dateTime: string,
  includeDate?: boolean,
  timeZoneName?: string
): string | undefined => {
  const utcString = getUTCDateString(dateTime)
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const targetTimeZone = timeZoneName || clientTimeZone
  const convertedTime = getLocalTime(utcString, targetTimeZone)

  if (!convertedTime) return undefined

  const date = new Date(convertedTime)
  const tzCode = getTimeZoneCode(convertedTime, targetTimeZone)

  return formatDateTime(date, { includeDate: !!includeDate, tzCode })
}

const getDwellTime = (
  arrivalTime: string | undefined | null,
  departureTime: string | undefined | null,
  estimatedArrivalTime: string | undefined | null,
  estimatedDepartureTime: string | undefined | null
): { dwellTime: number | undefined; isEstimate: boolean } => {
  let dwellTime: number | undefined = undefined
  let isEstimate = false

  if (arrivalTime && departureTime) {
    dwellTime = diffInSeconds(new Date(departureTime), new Date(arrivalTime))
  } else if (arrivalTime && estimatedDepartureTime) {
    dwellTime = diffInSeconds(new Date(estimatedDepartureTime), new Date(arrivalTime))
    isEstimate = true
  } else if (estimatedArrivalTime && estimatedDepartureTime) {
    dwellTime = diffInSeconds(
      new Date(estimatedDepartureTime),
      new Date(estimatedArrivalTime)
    )
    isEstimate = true
  }

  return { dwellTime, isEstimate }
}

const getLegTotalTime = (
  startTime: string | undefined | null,
  endTime: string | undefined | null,
  estimatedTripTime: number
): number => {
  if (endTime && startTime) {
    return diffInSeconds(new Date(endTime), new Date(startTime))
  }

  return estimatedTripTime
}

const getLocalTime = (
  time: string | undefined | null,
  timeZoneName: string | null | undefined
): string | undefined => {
  const localTime =
    time && convertToTimezone(time, timeZoneName ?? DEFAULT_TIME_ZONE_NAME)

  return localTime ?? undefined
}

/**
 * Helper that gets the ISO string representation of the current time minus 24 hours.
 *
 * @returns {string} The ISO string representation of the time 24 hours ago.
 */
const getNotificationTimeFloor = (): string => {
  return new Date(new Date().getTime() - 24 * 60 * 60 * 1000).toISOString()
}

const getLegColor = (legIdx: number) => LEG_COLORS[legIdx % LEG_COLORS.length].hexColor

const getSortingColumnName = (sortEnum: string): string => {
  const [...sortingColumnParts] = (sortEnum || '').split('_')

  return sortingColumnParts.slice(0, sortingColumnParts.length - 1).join('_')
}

const getLoadEmptyStateMessage = (isFilterEmpty: boolean) =>
  isFilterEmpty
    ? 'Look up a single load by typing in Ryder # or Reference # in a search bar, or view a list of loads by selecting cost centers and/or other filters.'
    : 'There are no loads based on your applied filters. Adjust your filters or search by reference number to view loads.'

const isOperationalStop = (locationName: string | null | undefined) => {
  return !locationName || locationName === OPERATIONAL_STOP
}

const getStopName = (
  name: string | null | undefined,
  isLayoverStop: boolean
): string => {
  if (isLayoverStop && (!name || name === OPERATIONAL_STOP)) {
    return 'Layover stop'
  }

  if (isOperationalStop(name)) {
    return OPERATIONAL_STOP
  }

  return name ?? OPERATIONAL_STOP
}

const formatOperationalStopAddress = (
  address:
    | {
        line1?: string | null
        line2?: string | null
        city?: string | null
        stateOrProvinceAbbrev?: string | null
        postalCode?: string | null
      }
    | null
    | undefined,
  point: GpsPoint | null | undefined
) => {
  if (!address && !point) return '\u2014'

  const formattedAddress = formatAddress(address)

  if (point && !address?.line1 && !address?.line2) {
    const latLon = `(${point.lat.toFixed(5)}, ${point.lon.toFixed(5)})`

    return `${formattedAddress} ${latLon}`
  }

  return formattedAddress
}

/**
 * Projected lateness for a stop that hasn't arrived or departed yet, based on ETA vs.
 * threshold. Unlike {@link getStopLateByDuration}, which reports whether a stop is
 * actually late right now, this is a prediction — callers are expected to stop using
 * it (and switch to `getStopLateByDuration`) once the stop is actually late, or has
 * since arrived/departed.
 *
 * Returns a positive number of seconds when running late, 0 otherwise.
 */
const getStopRunningLateByDuration = ({
  isEnRoute,
  arrivalTime,
  departureTime,
  estimatedArrivalTime,
  latenessThresholdTime,
}: {
  isEnRoute: boolean
  arrivalTime: string | null | undefined
  departureTime: string | null | undefined
  estimatedArrivalTime: string | null | undefined
  latenessThresholdTime: string | null | undefined
}): number => {
  if (!isEnRoute || isNotNil(arrivalTime) || isNotNil(departureTime)) return 0

  const isThresholdTimePastETA =
    isNotNil(latenessThresholdTime) &&
    isNotNil(estimatedArrivalTime) &&
    isDateBeforeComparison(latenessThresholdTime, estimatedArrivalTime)

  return isThresholdTimePastETA
    ? diffInSeconds(new Date(estimatedArrivalTime), new Date(latenessThresholdTime))
    : 0
}

/**
 * getStopRunningLateDurationOperator determines how long a stop is late and if so how
 * late for an operator.
 * Return positive number if late, 0 if not late.
 */
const getStopRunningLateDurationOperator = ({
  estimatedArrivalTime,
  latenessThresholdTime,
  status,
  arrivalTime,
  departureTime,
}: {
  estimatedArrivalTime: string | null | undefined
  latenessThresholdTime: string | null | undefined
  status: ShipmentStatus | LoadStatus | null | undefined
  arrivalTime: string | null | undefined
  departureTime: string | null | undefined
}): number =>
  getStopRunningLateByDuration({
    isEnRoute:
      isNotNil(status) &&
      [ShipmentStatus.PickedUp, LoadStatus.Started].includes(status),
    arrivalTime,
    departureTime,
    estimatedArrivalTime,
    latenessThresholdTime,
  })

const isStopLate = ({
  arrivalTime,
  departureTime,
  estimatedArrivalTime,
  latenessThresholdTime,
  status,
}: {
  arrivalTime: string | null | undefined
  departureTime: string | null | undefined
  estimatedArrivalTime: string | null | undefined
  latenessThresholdTime: string | null | undefined
  status: ShipmentStatus | LoadStatus | null | undefined
}): boolean => {
  const latenessDuration = getStopRunningLateDurationOperator({
    estimatedArrivalTime,
    latenessThresholdTime,
    status,
    arrivalTime,
    departureTime,
  })

  return latenessDuration > 0
}

/**
 * How late a stop already is against its lateness threshold, in seconds.
 *
 * Measured from the actual arrival when the stop has arrived, otherwise from
 * `currentTime` so a stop that is still en route keeps accruing lateness. This
 * reflects whether the stop is actually late right now, unlike
 * {@link getStopRunningLateDurationOperator}, which projects lateness from an ETA
 * and stops reporting once the stop has arrived.
 *
 * Returns a positive number of seconds when late, 0 otherwise.
 */
const getStopLateByDuration = (
  stop:
    | {
        arrivalTime?: string | null
        departureTime?: string | null
        latenessThresholdTime?: string | null
      }
    | null
    | undefined,
  currentTime: Date = new Date()
): number => {
  if (isNil(stop?.latenessThresholdTime)) return 0

  const actualTime = stop.arrivalTime ?? stop.departureTime
  const arrivedAt = isNotNil(actualTime) ? new Date(actualTime) : currentTime

  return Math.max(diffInSeconds(arrivedAt, new Date(stop.latenessThresholdTime)), 0)
}

const prettyDuration = (durationSeconds: number): string => {
  if (durationSeconds < 0) return ''

  const days = Math.floor(durationSeconds / (24 * 60 * 60))
  const hours = Math.floor((durationSeconds % (24 * 60 * 60)) / (60 * 60))
  const minutes = Math.floor((durationSeconds % (60 * 60)) / 60)

  const parts = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)

  return parts.length > 0 ? `${parts.join(' ')}` : '0m'
}

const getLateStopLabel = (
  durationSeconds: number,
  showExactDuration?: boolean
): string | undefined => {
  if (durationSeconds < 0) return undefined

  if (durationSeconds >= TWENTY_FOUR_HOURS_IN_SECONDS) {
    return `${Math.floor(durationSeconds / TWENTY_FOUR_HOURS_IN_SECONDS)}d late`
  }

  if (showExactDuration) {
    return `${prettyDuration(durationSeconds)} late`
  }

  return durationSeconds < 60 ? 'Late' : `${prettyDuration(durationSeconds)} late`
}

const getFirstLateStopWithLabel = (
  stopInfo: Array<{
    latenessStatus?: StopLatenessStatus | null
    appointmentEnd?: string | null
    arrivalTime?: string | null
  }>
): {
  latenessStatus: StopLatenessStatus | null | undefined
  label: string | undefined
} => {
  const firstLateStop = stopInfo.find(
    (stop) =>
      stop.latenessStatus === StopLatenessStatus.Late &&
      !isNil(stop.appointmentEnd) &&
      isNil(stop.arrivalTime)
  )

  if (isNotNil(firstLateStop)) {
    return {
      latenessStatus: StopLatenessStatus.Late,
      label: firstLateStop.appointmentEnd
        ? getLateStopLabel(
            diffInSeconds(new Date(), new Date(firstLateStop.appointmentEnd))
          )
        : undefined,
    }
  }

  const firstRunningLateStop = stopInfo.find(
    (stop) => stop.latenessStatus === StopLatenessStatus.RunningLate
  )

  return {
    latenessStatus: firstRunningLateStop?.latenessStatus,
    label: firstRunningLateStop
      ? enumToPretty(StopLatenessStatus.RunningLate)
      : undefined,
  }
}

const getOperatorLatenessLabel = (
  currentTime: Date,
  latenessStatus: StopLatenessStatus | null | undefined,
  appointmentEnd: string | null | undefined,
  arrivalTime: string | null | undefined
): string | undefined => {
  if (isNil(latenessStatus) || isNil(appointmentEnd) || isNotNil(arrivalTime))
    return undefined

  if (latenessStatus === StopLatenessStatus.Late) {
    return getLateStopLabel(diffInSeconds(currentTime, new Date(appointmentEnd)))
  } else if (latenessStatus === StopLatenessStatus.RunningLate) {
    return enumToPretty(StopLatenessStatus.RunningLate)
  }
}

const isLegStatusValidForLateness = (
  legStatus: LegStatus | null | undefined
): boolean => {
  return legStatus
    ? new Set([
        LegStatus.Available,
        LegStatus.Planned,
        LegStatus.Dispatched,
        LegStatus.Started,
      ]).has(legStatus)
    : false
}

const isStopValidForLateness = (stop: {
  status?: StopStatus | null
  appointmentEnd?: string | null
  arrivalTime?: string | null
}): boolean => {
  if (stop.status === StopStatus.Skipped || stop.status === StopStatus.Attempted) {
    return false
  }

  if (!stop.appointmentEnd) {
    return false
  }

  if (stop.arrivalTime) {
    return false
  }

  return true
}

const shouldDisplayLateness = ({
  load,
  leg,
  view,
}: {
  load?: LoadForViolations
  leg?: DetailedLegForViolations
  view: View
}): boolean => {
  const isRv3 = load?.isRv3 ?? leg?.isRv3Load ?? false

  // Only show lateness for RV3 loads/legs
  if (!isRv3) return false

  // Don't show lateness for Available loads in list view
  if (load?.status === LoadStatus.Available && view === View.ListView) {
    return false
  }

  return true
}

const getTractorTypeName = (type: string | null | undefined): string => {
  if (isNil(type)) return 'Unknown'

  return TRACTOR_TYPE_TO_NAME.get(type) ?? 'Unknown'
}

const injectStopReferences = <
  T extends {
    legs: Array<{ stops: Array<{ idx: number; location?: { id: bigint } | null }> }>
  }
>(
  load: T
): T & {
  legs: Array<{
    stops: Array<{
      idx: number
      location?: { id: bigint } | null
      stopReferences?: Array<StopReference> | undefined
    }>
  }>
} => {
  return {
    ...load,
    legs: load.legs.map((leg) => ({
      ...leg,
      stops: leg.stops.map((stop) => ({
        ...stop,
        stopReferences:
          'stopReferences' in stop
            ? ((stop as ListStop).stopReferences as Array<StopReference>)
            : [],
      })),
    })),
  }
}

const PICKUP_STOP_ACTIONS = new Set<ScosStopAction>([
  ScosStopAction.Lld,
  ScosStopAction.Hlt,
  ScosStopAction.Ihlt,
  ScosStopAction.Hpl,
])

const getFreightStatus = (
  stopStatus?: StopProgressStatus | null,
  osdRecordStatus?: OsdRecordStatus | null,
  scosStopAction?: ScosStopAction | null
) => {
  switch (stopStatus) {
    case StopProgressStatus.Cancelled:
      return FreightStatus.CANCELLED
    case StopProgressStatus.Skipped:
    case StopProgressStatus.Attempted:
    case StopProgressStatus.Planned:
    case StopProgressStatus.InTransit:
      return FreightStatus.PLANNED
    case StopProgressStatus.AtLocation:
    case StopProgressStatus.Completed:
      break
    default:
      throw new Error(`Unknown stop status: ${stopStatus}`)
  }

  switch (osdRecordStatus) {
    case OsdRecordStatus.Planned:
      return FreightStatus.PLANNED
    case OsdRecordStatus.Over:
      return FreightStatus.OVER
    case OsdRecordStatus.Short:
      return FreightStatus.SHORT
    case OsdRecordStatus.Damage:
      return FreightStatus.DAMAGED
    case OsdRecordStatus.Good:
    case undefined:
      break
    default:
      throw new Error(`Unknown OS&D status: ${osdRecordStatus}`)
  }

  if (isNil(osdRecordStatus) && stopStatus === StopProgressStatus.AtLocation) {
    return FreightStatus.PLANNED
  }

  if (PICKUP_STOP_ACTIONS.has(scosStopAction as ScosStopAction)) {
    return FreightStatus.PICKED_UP
  }

  return FreightStatus.DELIVERED
}

const getExceptionCreatedBy = (exception: ExceptionTypes): string | undefined => {
  if (
    !(
      exception.__typename === 'AttemptedStopException' ||
      exception.__typename === 'SkippedStopException'
    )
  ) {
    return
  }

  if (isNotNil(exception.driverCreatedBy)) {
    return 'recorded by driver'
  }

  if (isNotNil(exception.createdBy)) {
    return exception.createdBy.fullname ?? 'recorded by operator'
  }
}

/**
 * This function returns the notification risk tier corresponding to a load's lateness status,
 * useful for determining the relative importance between lateness and other violations.
 */
const getLoadLatenessTier = (
  legs: Array<LegForLatenessViolations> | null | undefined,
  legIdx: number | undefined,
  stopIdx: number | undefined
): RiskTier | null => {
  if (!legs) return null

  let loadLatenessTier: RiskTier | null = null

  for (const leg of legs) {
    if (isNotNil(legIdx) && leg.idx !== legIdx) continue

    if (!isLegStatusValidForLateness(leg.status)) {
      continue
    }

    for (const stop of leg.stops) {
      if (isNotNil(stopIdx) && stop.idx !== stopIdx) continue

      if (
        stop.status === StopStatus.Skipped ||
        stop.status === StopStatus.Attempted ||
        !stop.appointmentEnd ||
        stop.arrivalTime
      ) {
        continue
      }

      if (stop.latenessStatus === StopLatenessStatus.Late) {
        return RiskTier.Critical
      }

      if (stop.latenessStatus === StopLatenessStatus.RunningLate) {
        loadLatenessTier = RiskTier.Risk
      }
    }
  }

  return loadLatenessTier
}

const getLatenessTier = ({
  load,
  leg,
  stopId,
  view,
}: {
  load?: LoadForViolations
  leg?: DetailedLegForViolations
  stopId?: bigint
  view: View
}): RiskTier | null => {
  if (!shouldDisplayLateness({ load, leg, view })) {
    return null
  }

  const legs = load?.legs ?? (leg ? [leg] : [])
  let latenessTier: RiskTier | null = null

  for (const currentLeg of legs) {
    if (!isLegStatusValidForLateness(currentLeg.status)) {
      continue
    }

    for (const stop of currentLeg.stops) {
      if (isNotNil(stopId) && stop.id !== stopId) continue

      if (!isStopValidForLateness(stop)) {
        continue
      }

      if (stop.latenessStatus === StopLatenessStatus.Late) {
        return RiskTier.Critical
      }

      if (stop.latenessStatus === StopLatenessStatus.RunningLate) {
        latenessTier = RiskTier.Risk
      }
    }
  }

  return latenessTier
}

const isExceptionForViolation = (exception: ExceptionForViolations): boolean => {
  if (isNil(exception.__typename)) return false
  if (!VIOLATION_EXCEPTION_TYPES.includes(exception.__typename)) return false
  if (isNotNil(exception.rescheduledRyderNumber)) return false

  return true
}

/**
 * Extracts exceptions from either a load (load-level exceptions) or a leg (stop-level exceptions).
 * For load-level exceptions, filters by stopId if provided.
 * For stop-level exceptions, aggregates exceptions from all stops or a specific stop.
 */
const extractExceptions = (
  load: LoadForViolations | undefined,
  leg: DetailedLegForViolations | undefined,
  stopId: bigint | undefined
): Array<ExceptionForViolations> => {
  if (load) {
    const loadExceptions = load.exceptions || []
    if (isNotNil(stopId)) {
      return loadExceptions
        .filter((exception) => exception.stopId === stopId)
        .filter(isExceptionForViolation)
    }

    return loadExceptions.filter(isExceptionForViolation)
  }

  if (leg) {
    const allStopExceptions: Array<ExceptionForViolations> = []
    for (const stop of leg.stops) {
      if (isNotNil(stopId) && stop.id !== stopId) continue
      for (const exception of stop.exceptions || []) {
        if (isExceptionForViolation(exception)) {
          allStopExceptions.push(exception)
        }
      }
    }

    return allStopExceptions
  }

  return []
}

const getViolationHoverText = (violations: Array<Violation>): string => {
  if (violations.length === 0) return ''

  // Sort by legIdx, then stopIdx, then createdAt (newest first within each group)
  // Violations without legIdx/stopIdx go to the back
  const sortedViolations = [...violations].sort((first, second) => {
    // Sort by legIdx first
    if (first.legIdx !== second.legIdx) {
      if (isNil(first.legIdx)) return 1
      if (isNil(second.legIdx)) return -1

      return first.legIdx - second.legIdx
    }

    // Then by stopIdx
    if (first.stopIdx !== second.stopIdx) {
      if (isNil(first.stopIdx)) return 1
      if (isNil(second.stopIdx)) return -1

      return first.stopIdx - second.stopIdx
    }

    // Then by createdAt (newest first)
    if (!first.createdAt && !second.createdAt) return 0
    if (!first.createdAt) return 1
    if (!second.createdAt) return -1

    return new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime()
  })

  // Group violations by type, collecting names for LateArrival
  const violationMap = new Map<NotificationType | ExceptionTypename, Set<string>>()

  for (const violation of sortedViolations) {
    const existingNames = violationMap.get(violation.type) || new Set<string>()
    if (violation.name) {
      existingNames.add(violation.name)
    }
    violationMap.set(violation.type, existingNames)
  }

  // Build the hover text
  const hoverTexts: Array<string> = []

  const entries = Array.from(violationMap.entries())
  for (const [type, names] of entries) {
    const displayName = ViolationDisplayText[type] || type

    if (type === 'LateArrivalException' && names.size > 0) {
      hoverTexts.push(`${displayName} (${Array.from(names).join(', ')})`)
    } else {
      hoverTexts.push(displayName)
    }
  }

  return hoverTexts.join(', ')
}

const exceptionToViolation = (
  exception: ExceptionForViolations,
  legs: Array<LegForViolations>
): Violation | null => {
  if (!exception.__typename) return null

  let legIdx: number | undefined
  let stopIdx: number | undefined
  for (const leg of legs) {
    const stop = leg.stops.find((stop) => stop.id === exception.stopId)
    if (stop) {
      legIdx = leg.idx
      stopIdx = stop.idx
      break
    }
  }

  return {
    tier: RiskTier.Critical,
    type: exception.__typename,
    createdAt: exception.createdAt,
    legIdx,
    stopIdx,
    name: exception.stopLateReason?.name,
  }
}

const getViolationIcon = (tier: RiskTier, useWhiteIcon?: boolean) => {
  const icon = iconMap[tier]

  if (useWhiteIcon && isValidElement<IconProps>(icon)) {
    return cloneElement(icon, { color: '#fff' })
  }

  return icon
}

const getLateArrivalException = <T extends { __typename?: string }>(
  exceptions: Array<T>
) =>
  exceptions.find(
    (exception): exception is Extract<T, { __typename?: 'LateArrivalException' }> =>
      exception.__typename === 'LateArrivalException'
  )

const isLayoverStop = (stop: {
  type?: string | null
  scosAction?: ScosStopAction | null
}) => stop.type === 'LAYO' && stop.scosAction === ScosStopAction.Trp

const getActiveLeg = <TLeg extends { status?: LegStatus }>(load: {
  legs: Array<TLeg>
}): TLeg | undefined => {
  let lastCompletedLeg: TLeg | undefined
  let nextLeg: TLeg | undefined

  for (let i = 0; i < load.legs.length; i++) {
    const leg = load.legs[i]

    if (leg.status === LegStatus.Completed) {
      lastCompletedLeg = leg
      nextLeg = load.legs[i + 1]
    }
  }

  if (!lastCompletedLeg) {
    return load.legs[0]
  }

  if (
    nextLeg &&
    nextLeg.status &&
    [LegStatus.Dispatched, LegStatus.Started].includes(nextLeg.status)
  ) {
    return nextLeg
  }

  return lastCompletedLeg
}

/**
 *
 * @param stops The array of stops containing trailer and trailer-segment information.
 * @returns A map of stop IDs to trailer changes (`from` / `to` unit numbers), keyed only
 *   at trailer segment starts after the first segment.
 */
const getTrailerChangesByStop = (
  stops: Array<{
    id: bigint
    trailer?: { unitNumber?: string | null; id: number } | null
    trailerSegmentIdx?: number | null
    isTrailerSegmentStart?: boolean
  }>
) => {
  const results = new Map<
    bigint,
    {
      from?: string | null
      to?: string | null
    }
  >()

  let currentTrailer: string | null = null

  for (let i = 0; i < stops.length; i++) {
    const stop = stops[i]

    if (!stop.isTrailerSegmentStart) {
      continue
    }

    const previousTrailer: string | null = currentTrailer

    currentTrailer = stop.trailer?.unitNumber ?? null

    if (stop.trailerSegmentIdx === 1) {
      continue
    }

    if (!previousTrailer && !currentTrailer) {
      continue
    }

    if (previousTrailer === currentTrailer) {
      continue
    }

    results.set(stop.id, {
      from: previousTrailer,
      to: currentTrailer,
    })
  }

  return results
}

const getListOfTrailers = <TStop extends object>(
  stops: Array<TStop>,
  {
    getTrailer,
  }: {
    getTrailer: (stop: TStop) => TrailerInfo | null | undefined
  }
): Array<TrailerInfo> => {
  return Array.from(
    new Map(
      stops
        .map(getTrailer)
        .filter((trailer): trailer is TrailerInfo => isNotNil(trailer?.unitNumber))
        .map((trailer) => [trailer.unitNumber, trailer])
    ).values()
  )
}

const getLoadTrailers = <TLoad, TStop extends object>(
  load: TLoad,
  {
    getStops,
    getTrailer,
  }: {
    getStops: (load: TLoad) => Array<TStop>
    getTrailer: (stop: TStop) => TrailerInfo | null | undefined
  }
): Array<TrailerInfo> => {
  return getListOfTrailers(getStops(load), { getTrailer })
}

export {
  capitalize,
  formatOperationalStopAddress,
  getActiveLeg,
  getBestProofOfDeliveryDoc,
  getDateAndTimeFromDatetime,
  getDocumentFilterType,
  getDwellTime,
  getFirstLateStopWithLabel,
  getLateStopLabel,
  getLegColor,
  getLegTotalTime,
  getLoadEmptyStateMessage,
  getLocaleDateString,
  getLocaleDateTimeString,
  getLocaleTimeString,
  getLocalTime,
  getNotificationTimeFloor,
  getOperatorLatenessLabel,
  getStopName,
  isLegStatusValidForLateness,
  isStopValidForLateness,
  shouldDisplayLateness,
  getOppositeDirection,
  getSortingColumnName,
  getStopLateByDuration,
  getStopRunningLateDurationOperator,
  getStopRunningLateByDuration,
  getLoadLatenessTier,
  getLatenessTier,
  isExceptionForViolation,
  extractExceptions,
  getViolationHoverText,
  exceptionToViolation,
  getViolationIcon,
  getLateArrivalException,
  getTimeZoneCode,
  getTractorTypeName,
  getUTCDateString,
  injectStopReferences,
  isLayoverStop,
  isOperationalStop,
  isStopLate,
  prettyDuration,
  getFreightStatus,
  getExceptionCreatedBy,
  getTrailerChangesByStop,
  getListOfTrailers,
  getLoadTrailers,
}
